import { Card, Colors, Icon, Text } from '@amelio/ui-library';
import { useState } from 'react';

import { SelectedEmployeeSummary } from './SelectedEmployeeSummary';
import { TopicTiles } from './TopicTiles';

import type { ThematicMeetingTopic } from '@/types/thematic-meetings';

import { useGetMeetingTemplates } from '@/api/thematic-meetings';
import { EmployeeSelector } from '@/components/EmployeeSelector/EmployeeSelector';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { useAmelioTranslations } from '@/hooks';

interface EmployeeAndTopicStepProps {
    employeeId: string;
    topic: ThematicMeetingTopic | null;
    /** An employee already picked before the modal opened — forwarded to `EmployeeSelector`, which
     * resolves it and reports it back through `onEmployeeChange` on its own. */
    initialEmployeeId?: string;
    onEmployeeChange: (employeeId: string) => void;
    onSelectTopic: (topic: ThematicMeetingTopic) => void;
}

/** Where the focus goes when the step opens and after the employee changes, so a keyboard user never
 * lands on the dialog body. */
type FocusTarget = 'change' | 'search' | null;

/**
 * First step of the creation flow: who the meeting is with, and what it is about. Once an employee is
 * chosen, the search folds into a one-line summary and the topic tiles appear below it, so the step
 * reads as two ordered questions.
 */
export const EmployeeAndTopicStep = ({
    employeeId,
    topic,
    initialEmployeeId,
    onEmployeeChange,
    onSelectTopic,
}: EmployeeAndTopicStepProps) => {
    const { t } = useAmelioTranslations();
    // The catalog the tiles are built from. Read here too, so the step can say the question cannot be
    // asked instead of showing two empty topic groups and a forward button disabled for good.
    const {
        isError: isTemplatesError,
        isFetching: isFetchingTemplates,
        refetch: refetchTemplates,
    } = useGetMeetingTemplates();
    // With nobody handed over, the step's first question is "who?" and its only answer is typed:
    // the search takes the keystroke from the start. A preselection answers it, so the dialog keeps
    // its own first focus.
    const [focusTarget, setFocusTarget] = useState<FocusTarget>(
        initialEmployeeId ? null : 'search',
    );
    // The selector picks the preselected employee again whenever it mounts: once the manager has
    // asked to change them, it must stop doing so.
    const [isPreselectionCleared, setIsPreselectionCleared] = useState(false);

    const handleChangeEmployee = () => {
        setIsPreselectionCleared(true);
        setFocusTarget('search');
        onEmployeeChange('');
    };

    const handleSelectEmployee = (nextEmployeeId: string) => {
        // A preselection resolving on open is not the manager's gesture: the dialog keeps its own
        // first focus.
        const isManagerChoice = !initialEmployeeId || isPreselectionCleared;
        setFocusTarget(isManagerChoice ? 'change' : null);
        onEmployeeChange(nextEmployeeId);
    };

    return (
        <>
            {employeeId ? (
                <SelectedEmployeeSummary
                    employeeId={employeeId}
                    focusOnMount={focusTarget === 'change'}
                    onChange={handleChangeEmployee}
                />
            ) : (
                <Card
                    bgColor={'bg-base-25'}
                    borderColor={Colors.BASE_100}
                    borderRadius={'xl'}
                    padding={'lg'}
                >
                    <div className={'flex items-start gap-3'}>
                        <Icon
                            name={'users-user-edit'}
                            size={'lg'}
                            color={Colors.NAVY}
                            ariaHidden
                        />

                        <div className={'flex flex-1 flex-col gap-2'}>
                            <Text
                                weight={'medium'}
                                className={'text-[16px]'}
                            >
                                {t('thematicMeetings.newMeeting.employeeToMeet')}
                            </Text>

                            <EmployeeSelector
                                selectedEmployeeId={null}
                                preselectedEmployeeId={
                                    isPreselectionCleared ? undefined : initialEmployeeId
                                }
                                requireCompetencyProfile={false}
                                suggestOnFocus
                                focusOnMount={focusTarget === 'search'}
                                onSelectEmployee={(employee) => handleSelectEmployee(employee.id)}
                                onDeselectEmployee={() => onEmployeeChange('')}
                            />
                        </div>
                    </div>
                </Card>
            )}

            {employeeId && (
                // 8px on top of the step's own 24px gap puts the question 32px under the summary,
                // and 32px separate it from its tiles.
                <div className={'mt-[8px] flex flex-col gap-[32px]'}>
                    <Text
                        size={'large'}
                        weight={'semibold'}
                        color={Colors.NAVY}
                    >
                        {t('thematicMeetings.newMeeting.selectObjective')}
                    </Text>

                    {isTemplatesError ? (
                        <EmptyState
                            message={t('thematicMeetings.newMeeting.topicsLoadError')}
                            action={{
                                label: t('thematicMeetings.newMeeting.topicsRetry'),
                                onClick: () => void refetchTemplates(),
                                disabled: isFetchingTemplates,
                            }}
                            className={'py-8'}
                        />
                    ) : (
                        <TopicTiles
                            selectedTopic={topic}
                            onSelectTopic={onSelectTopic}
                        />
                    )}
                </div>
            )}

            {(!employeeId || !topic) && (
                <div className={'mt-auto'}>
                    <Text
                        size={'small'}
                        color={Colors.BASE_500}
                    >
                        {t('thematicMeetings.newMeeting.firstStepRequirements')}
                    </Text>
                </div>
            )}
        </>
    );
};
